import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { GraphStateService } from "../core-module/services/state/graph-state.service";
import { renderOtpDeratingGraph } from "../shared-module/graph-utils/otp-derating-graph";

// Fixed min-dim level for DTP (10% per spec)
const DTP_MIN_DIM = 10;
// X axis max temperature
const X_MAX_TEMP = 120;

@Component({
  standalone: false,
  selector: "app-otp-dtp-graph",
  templateUrl: "./otp-dtp-graph.component.html",
  styleUrls: ["./otp-dtp-graph.component.scss"],
})
export class OtpDtpGraphComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Kept as explicit public state for template/tests and API compatibility.
  readonly inputXRange: [number, number] = [0, X_MAX_TEMP];
  readonly inputYRange: [number, number] = [0, 100];

  // Default data (83-87 range)
  data: any[] = [
    { x: 0,          y: 100 },
    { x: 83,         y: 100 },
    { x: 87,         y: DTP_MIN_DIM },
    { x: X_MAX_TEMP, y: DTP_MIN_DIM },
  ];

  constructor(private graphState: GraphStateService) {}

  ngOnInit() {
    this.buildGraph();

    this.graphState.getOtpDtpGraphData
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        if (payload) {
          this.data = this.buildDataPoints(payload.dimStart, payload.dimStop);
        }
        this.buildGraph();
      });
  }

  /**
   * Build 4-point derating curve for DTP:
   *  [0] x=0,        y=100%       (cold temperature, full brightness)
   *  [1] x=dimStart, y=100%       (83 or 92 — dimming starts)
   *  [2] x=dimStop,  y=10%        (87 or 97 — max derating, fixed)
   *  [3] x=X_MAX,    y=10%        (stays at min dim)
   */
  private buildDataPoints(dimStart: number, dimStop: number): any[] {
    const start = Math.min(X_MAX_TEMP, Math.max(0, dimStart));
    const stop  = Math.min(X_MAX_TEMP, Math.max(0, dimStop));
    return [
      { x: 0,          y: 100 },
      { x: start,      y: 100 },
      { x: stop,       y: DTP_MIN_DIM },
      { x: X_MAX_TEMP, y: DTP_MIN_DIM },
    ];
  }

  private buildGraph() {
    renderOtpDeratingGraph(this.data, {
      svgId: "otpDtpGraphSvg",
      gradientId: "otpDtpGradient",
      xAxisLabel: "Temperature (&deg;C)",
      xAxisLabelAsHtml: true,
      chartHeight: 300,
      xDomain: this.inputXRange,
      yDomain: this.inputYRange,
      xTicks: 12,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
